/**
 * User Preferences API Client
 * 
 * Client for interacting with the user-preferences API.
 * Uses tokens provided by the host application (following UI.Streaks pattern).
 */

import axios, { AxiosInstance } from 'axios';

/**
 * API client for user preferences endpoints.
 * 
 * This client expects tokens to be provided by the host application.
 * It does not handle authentication itself - tokens should be passed to each method.
 */
export class UserPreferencesApiClient {
  private axiosInstance: AxiosInstance;
  private readonly BASE_URL = process.env.REACT_APP_USER_PREFERENCES_API_URL;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: this.BASE_URL,
      timeout: 30000, // 30 second timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Get user notification preferences by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param accessToken - The access token provided by the host application
   * @returns Promise with array of user notification preferences
   * @throws AxiosError if the request fails
   */
  async getUserPreferences(refUserId: number, accessToken: string): Promise<any[]> {
    try {
      const response = await this.axiosInstance.get(
        // TODO: Consider parameterizing the URL path based on context.
        `/user-notification-preferences/external_ref_user_id/${refUserId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }

  /**
   * Get LoreBot consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param accessToken - The access token provided by the host application
   * @returns Promise with boolean indicating consent status
   * @throws AxiosError if the request fails
   */
  async getLoreBotConsent(refUserId: number, accessToken: string): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get(
        `/privacy/consent/lorebot/ref_user_id/${refUserId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }

  /**
   * Get Account Information Sharing consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param accessToken - The access token provided by the host application
   * @returns Promise with boolean indicating consent status
   * @throws AxiosError if the request fails
   */
  async getAccountInformationSharingConsent(refUserId: number, accessToken: string): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get(
        `/privacy/consent/account-information-sharing/ref_user_id/${refUserId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }

  /**
   * Get LoreBot Chat Activity Sharing consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param accessToken - The access token provided by the host application
   * @returns Promise with boolean indicating consent status
   * @throws AxiosError if the request fails
   */
  async getLoreBotChatActivitySharingConsent(refUserId: number, accessToken: string): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get(
        `/privacy/consent/lorebot-chat-activity-sharing/ref_user_id/${refUserId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }

  /**
   * Update LoreBot consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param consent - The consent boolean value to set
   * @param accessToken - The access token provided by the host application
   * @returns Promise with the response object (status code available via response.status)
   * @throws AxiosError if the request fails
   */
  async updateLoreBotConsent(
    refUserId: number,
    consent: boolean,
    accessToken: string
  ): Promise<{ status: number }> {
    return this._updateConsent(refUserId, 'lorebot', consent, accessToken);
  }


  /**
   * Update Account Information Sharing consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param consent - The consent boolean value to set
   * @param accessToken - The access token provided by the host application
   * @returns Promise with the response object (status code available via response.status)
   * @throws AxiosError if the request fails
   */
  async updateAccountInformationSharingConsent(
    refUserId: number,
    consent: boolean,
    accessToken: string
  ): Promise<{ status: number }> {
    return this._updateConsent(refUserId, 'account-information-sharing', consent, accessToken);
  }

  /**
   * Update LoreBot Chat Activity Sharing consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param consent - The consent boolean value to set
   * @param accessToken - The access token provided by the host application
   * @returns Promise with the response object (status code available via response.status)
   * @throws AxiosError if the request fails
   */
  async updateLoreBotChatActivitySharingConsent(
    refUserId: number,
    consent: boolean,
    accessToken: string
  ): Promise<{ status: number }> {
    return this._updateConsent(refUserId, 'lorebot-chat-activity-sharing', consent, accessToken);
  }

  /**
   * Get Aetna Optional Information Sharing consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param accessToken - The access token provided by the host application
   * @returns Promise with boolean indicating consent status
   * @throws AxiosError if the request fails
   */
  async getAetnaOptionalInformationSharingConsent(refUserId: number, accessToken: string): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get(
        `/privacy/consent/aetna-optional-information-sharing/ref_user_id/${refUserId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }

  /**
   * Update Aetna Optional Information Sharing consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param consent - The consent boolean value to set
   * @param accessToken - The access token provided by the host application
   * @returns Promise with the response object (status code available via response.status)
   * @throws AxiosError if the request fails
   */
  async updateAetnaOptionalInformationSharingConsent(
    refUserId: number,
    consent: boolean,
    accessToken: string
  ): Promise<{ status: number }> {
    return this._updateConsent(refUserId, 'aetna-optional-information-sharing', consent, accessToken);
  }

  /**
   * Update consent status by reference user ID.
   * 
   * @param refUserId - The reference user ID
   * @param consent_type - The consent type
   * @param consent - The consent boolean value to set
   * @param accessToken - The access token provided by the host application
   * @returns Promise with the response object (status code available via response.status)
   * @throws AxiosError if the request fails
   */
  async _updateConsent(
    refUserId: number,
    consent_type: string,
    consent: boolean,
    accessToken: string): Promise<{ status: number }> {
    try {
      const response = await this.axiosInstance.put(
        `/privacy/consent/${consent_type}/ref_user_id/${refUserId}`,
        { consent },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return { status: response.status };
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }
}

// Export a singleton instance for convenience
export const userPreferencesApiClient = new UserPreferencesApiClient();

